import { useLayoutEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';

import type { OnboardingQuestion, ProfileData } from '../../lib/onboardingProfile';
import { QUESTION_SIDEBAR_LABELS, isStepCompleted } from '../../lib/onboardingProfile';
import { onboardingEase, onboardingMotion } from '../../lib/onboardingTheme';
import { cn } from '../../lib/utils';

const STEP_PITCH = 124;

interface OnboardingStepWheelProps {
  questions: OnboardingQuestion[];
  currentStep: number;
  formData: ProfileData;
  onGoToStep: (index: number) => void;
  className?: string;
}

function wheelVisuals(offset: number, reducedMotion: boolean) {
  const dist = Math.abs(offset);
  if (reducedMotion) {
    return {
      scale: dist === 0 ? 1 : 0.9,
      opacity: dist === 0 ? 1 : dist === 1 ? 0.5 : dist === 2 ? 0.32 : 0.2,
      rotateY: 0,
      z: 0,
      filter: 'none',
    };
  }
  return {
    scale: dist === 0 ? 1 : dist === 1 ? 0.9 : dist === 2 ? 0.82 : 0.74,
    opacity: dist === 0 ? 1 : dist === 1 ? 0.52 : dist === 2 ? 0.3 : 0.14,
    rotateY: offset * 26,
    z: -dist * 14,
    filter: dist > 1 ? `blur(${Math.min((dist - 1) * 1.25, 3)}px)` : 'blur(0px)',
  };
}

export default function OnboardingStepWheel({
  questions,
  currentStep,
  formData,
  onGoToStep,
  className,
}: OnboardingStepWheelProps) {
  const reducedMotion = useReducedMotion();
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState(0);

  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const measure = () => setViewportWidth(el.offsetWidth);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const trackOffset =
    viewportWidth > 0 ? viewportWidth / 2 - currentStep * STEP_PITCH - STEP_PITCH / 2 : 0;

  return (
    <motion.div
      ref={viewportRef}
      className={cn(
        'relative w-full overflow-hidden py-1 motion-reduce-off',
        'onboarding-step-wheel-mask',
        className,
      )}
      style={{ perspective: reducedMotion ? undefined : 900 }}
      aria-label="Onboarding steps"
    >
      <motion.div
        className="flex items-center will-change-transform"
        animate={{ x: trackOffset }}
        transition={{
          duration: reducedMotion ? 0.01 : onboardingMotion.duration + 0.1,
          ease: onboardingEase,
        }}
      >
        {questions.map((q, idx) => {
          const offset = idx - currentStep;
          const dist = Math.abs(offset);
          const isCurrent = dist === 0;
          const isCompleted =
            isStepCompleted(idx, formData, questions) && idx < currentStep;
          const label = QUESTION_SIDEBAR_LABELS[q.id] ?? q.id;
          const visuals = wheelVisuals(offset, !!reducedMotion);

          return (
            <motion.button
              key={q.id}
              type="button"
              onClick={() => onGoToStep(idx)}
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                'motion-reduce-off shrink-0 flex flex-col items-center justify-center gap-2.5 px-3 py-2 rounded-2xl',
                'focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30',
                isCurrent ? 'cursor-default' : 'cursor-pointer hover:opacity-90',
              )}
              style={{
                width: STEP_PITCH,
                transformStyle: 'preserve-3d',
                transformOrigin: 'center center',
              }}
              animate={visuals}
              transition={{
                duration: reducedMotion ? 0.01 : onboardingMotion.duration,
                ease: onboardingEase,
              }}
            >
              <motion.div
                className={cn(
                  'flex h-[18px] w-[18px] items-center justify-center rounded-full border transition-colors',
                  isCompleted && 'border-white/35 bg-white/10',
                  isCurrent &&
                    'border-[#C5CED9] bg-white/15 shadow-[0_0_14px_rgba(232,237,244,0.4)]',
                  !isCompleted &&
                    !isCurrent &&
                    'border-white/15 bg-white/[0.04]',
                )}
                animate={{
                  scale: isCurrent ? 1.08 : 1,
                }}
                transition={{ duration: 0.35, ease: onboardingEase }}
              >
                {isCompleted ? (
                  <Check className="h-2.5 w-2.5 text-[#E8EDF4]" strokeWidth={2.5} />
                ) : isCurrent ? (
                  <motion.div className="h-1.5 w-1.5 rounded-full bg-[#E8EDF4]" />
                ) : (
                  <div className="h-1 w-1 rounded-full bg-white/25" />
                )}
              </motion.div>
              <span
                className={cn(
                  'w-full truncate text-center text-[10px] uppercase tracking-[0.12em] leading-tight transition-colors',
                  isCurrent
                    ? 'font-medium text-[#F4F6F8]'
                    : isCompleted
                      ? 'text-[#9AA8B8]'
                      : dist <= 2
                        ? 'text-[#6B7588]'
                        : 'text-[#4A5568]',
                )}
              >
                {label}
              </span>
            </motion.button>
          );
        })}
      </motion.div>
    </motion.div>
  );
}
